import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useModuleQuestions, AdminQuizQuestion } from '@/hooks/useEducationAdmin';
import { QuestionCard } from './components/QuestionCard';
import { QuestionEditDialog } from './QuestionEditDialog';

interface QuizQuestionManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleId: string;
}

export const QuizQuestionManager: React.FC<QuizQuestionManagerProps> = ({
  open,
  onOpenChange,
  moduleId,
}) => {
  const { data: questions = [], isLoading } = useModuleQuestions(moduleId);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<AdminQuizQuestion | null>(null);

  const handleCreate = () => {
    setEditingQuestion(null);
    setEditDialogOpen(true);
  };

  const handleEdit = (question: AdminQuizQuestion) => {
    setEditingQuestion(question);
    setEditDialogOpen(true);
  };

  if (!moduleId) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Manage Quiz Questions</DialogTitle>
            <DialogDescription>
              Create and manage quiz questions for this module.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {questions.length} question(s)
              </div>
              <Button onClick={handleCreate} size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add Question
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : questions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No questions found. Create your first question to get started.</p>
              </div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto space-y-3">
                {questions.map((question, index) => (
                  <QuestionCard
                    key={question.id}
                    question={question}
                    index={index}
                    onEdit={handleEdit}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <QuestionEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        question={editingQuestion}
        moduleId={moduleId}
      />
    </>
  );
};